import { maybe } from "@saleor/misc";
import { SmopOrderStatusEnum } from "@saleor/types/globalTypes";

import { StoreOrderUpdate } from "./types/StoreOrderUpdate";

const {
  ACKNOWLEDGED,
  READY,
  COLLECTED,
  RECEIVED_AT_FC,
  COMPLETED,
  CANCELED
} = SmopOrderStatusEnum;

export const handleUpdateStatusNotify = (
  data: StoreOrderUpdate,
  storeStatus,
  navigate,
  refetch,
  storeOrderUrl,
  id,
  notify
) => {
  const updated_status = data?.updateStoreOrderStatus?.storeOrder?.smopStatus
    ? data?.updateStoreOrderStatus?.storeOrder?.smopStatus
    : storeStatus;
  switch (updated_status) {
    // if success, set message according to case
    // if error, set message according to the new status
    case ACKNOWLEDGED:
      if (!maybe(() => data.updateStoreOrderStatus.errors.length !== 0)) {
        notify({
          status: "success",
          text:
            "Order " +
            data.updateStoreOrderStatus.storeOrder.orderCode +
            " has been acknowledged."
        });
      } else if (data.updateStoreOrderStatus.errors[0].field === "parcelNo") {
        notify({
          status: "error",
          text: "Unable to mark as ready. Please input Parcel ID."
        });
      } else if (data.updateStoreOrderStatus.errors[0].field === "lineStatus") {
        notify({
          status: "error",
          text: data.updateStoreOrderStatus.errors[0].message
        });
      } else {
        notify({
          status: "error",
          text: "Something Went Wrong"
        });
      }
      break;
    case READY:
      if (!maybe(() => data.updateStoreOrderStatus.errors.length !== 0)) {
        notify({
          status: "success",
          text:
            "Order " +
            data.updateStoreOrderStatus.storeOrder.orderCode +
            " is now ready for collection."
        });
      } else if (data.updateStoreOrderStatus.errors[0].field === "parcelNo") {
        notify({
          status: "error",
          text: "Unable to collect. Please validate first the Parcel ID."
        });
      } else {
        notify({
          status: "error",
          text: "Something Went Wrong"
        });
      }
      break;
    case COLLECTED:
      if (!maybe(() => data.updateStoreOrderStatus.errors.length !== 0)) {
        notify({
          status: "success",
          text:
            "Order " +
            data.updateStoreOrderStatus.storeOrder.orderCode +
            " has been collected. You may drop it off now at the Fulfillment Center."
        });
      } else if (data.updateStoreOrderStatus.errors[0].field === "parcelNo") {
        notify({
          status: "error",
          text: "Unable to receive. Please validate first the Parcel ID."
        });
      } else {
        notify({
          status: "error",
          text: "Something Went Wrong"
        });
      }
      break;
    case RECEIVED_AT_FC:
      if (!maybe(() => data.updateStoreOrderStatus.errors.length !== 0)) {
        notify({
          status: "success",
          text: `Order ${data.updateStoreOrderStatus.storeOrder.orderCode} has been Received at FC.`
        });
      } else if (data.updateStoreOrderStatus.errors[0].field === "parcelNo") {
        notify({
          status: "error",
          text: "Unable to receive at FC. Please validate first the Parcel ID."
        });
      } else {
        notify({
          status: "error",
          text: "Something Went Wrong"
        });
      }
      break;
    case COMPLETED:
      if (!maybe(() => data.updateStoreOrderStatus.errors.length !== 0)) {
        notify({
          status: "success",
          text: `Order ${data.updateStoreOrderStatus.storeOrder.orderCode} has been marked as completed.`
        });
      } else if (data.updateStoreOrderStatus.errors[0].field === "parcelNo") {
        notify({
          status: "error",
          text:
            "Unable to mark as completed. Please validate first the Parcel ID."
        });
      } else {
        notify({
          status: "error",
          text: "Something Went Wrong"
        });
      }
      break;
    default:
      if (!maybe(() => data.updateStoreOrderStatus.errors.length !== 0)) {
        if (data.updateStoreOrderStatus.storeOrder.smopStatus === CANCELED) {
          notify({
            status: "success",
            text:
              "Order " +
              data.updateStoreOrderStatus.storeOrder.orderCode +
              " has been cancelled."
          });
        } else {
          notify({
            status: "success",
            text:
              "Order " +
              data.updateStoreOrderStatus.storeOrder.orderCode +
              " has been " +
              data.updateStoreOrderStatus.storeOrder.smopStatus.toLowerCase()
          });
        }
      } else if (
        data.updateStoreOrderStatus.errors[0].field === "cancelReason"
      ) {
        notify({
          status: "error",
          text: "Unable to cancel. Please provide the reason of cancellation."
        });
      } else {
        notify({
          status: "error",
          text: "Something Went Wrong"
        });
      }
      break;
  }
  if (navigate !== null) {
    navigate(storeOrderUrl(id));
  }
  refetch();
};

export const onSubmitParcelNo = (parcelNo, setParcelNo, id) => {
  setParcelNo({
    variables: {
      id,
      parcelNo
    }
  });
};

export const onSubmitRunnerScan = (parcelNo, runnerScanParcel, id) => {
  runnerScanParcel({
    variables: {
      id,
      parcelNo
    }
  });
};

export const onSubmitOpsScan = (parcelNo, opsScanParcel, id) => {
  opsScanParcel({
    variables: {
      id,
      parcelNo
    }
  });
};

export const onSubmitRemoveProduct = (lineIds, removeProduct, id) => {
  removeProduct({
    variables: {
      lineIds,
      storeOrderId: id
    }
  });
};

export const onSubmitAddProduct = (product, addProduct, id, tenantId) => {
  addProduct({
    variables: {
      lines: [
        {
          quantity: product.quantity,

          variantId: product.variantId
        }
      ],
      storeOrderId: id,
      tenantId
    }
  });
};

export const onSubmitOrderLineStatus = (
  lineIds,
  status,
  updateOrderLineStatus,
  id
) => {
  updateOrderLineStatus({
    variables: {
      lineIds,
      status,
      storeOrderId: id
    }
  });
};

export const onSubmitOrderLineUpdateQty = (
  lines,
  updateOrderLineQuantity,
  id
) => {
  updateOrderLineQuantity({
    variables: {
      lines,
      storeOrderId: id
    }
  });
};

export const filterIsVisibilityProducts = products =>
  products
    ? products.filter(
        product =>
          product.node.productVisibility &&
          product.node.productVisibility.isVisible === true
      )
    : [];

export const storeOrderMutationComplete = (
  mutationType,
  data,
  notify,
  navigate,
  storeOrderUrl,
  id,
  refetch
) => {
  switch (mutationType) {
    case "ASSIGN_RUNNER":
      if (data.assignStoreOrderRunner.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Runner has been assigned`
        });
        if (navigate !== null) {
          navigate(storeOrderUrl(id));
        }
        refetch();
      } else {
        notify({
          status: "error",
          text: `Order status must be set to READY`
        });
      }
      refetch();
      break;
    case "SET_PARCEL":
      if (data.storeOrderSetParcelNo.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `The parcel ID has been assigned`
        });
        navigate(storeOrderUrl(id));
        refetch();
      } else {
        notify({
          status: "error",
          text: `${data.storeOrderSetParcelNo.orderErrors[0].message}`
        });
      }
      break;
    case "RUNNER_SCAN_PARCEl":
      if (data.storeOrderRunnerScanParcel.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Scanned Parcel ID matched the associated Parcel ID by the Store.`
        });
      } else {
        notify({
          status: "error",
          text: `Parcel ID is incorrect. Please check with the Store.`
        });
      }
      navigate(storeOrderUrl(id));
      refetch();
      break;
    case "OPS_SCAN_PARCEL":
      if (data.storeOrderOpsAdminScanParcel.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Scanned Parcel ID matched the associated Parcel ID by the Store.`
        });
      } else {
        notify({
          status: "error",
          text: `Parcel ID is incorrect. Please check with the Store.`
        });
      }
      navigate(storeOrderUrl(id));
      refetch();
    case "REMOVE_PRODUCT":
      if (data.storeOrderLineDelete.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `The selected product has been removed from the order.`
        });
      } else {
        notify({
          status: "error",
          text: `Something went wrong`
        });
      }
      navigate(storeOrderUrl(id));
      refetch();
    case "ADD_PRODUCT":
      if (data.storeOrderLinesAdd.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `The selected product has been added to the order.`
        });
      } else {
        notify({
          status: "error",
          text: `${data.storeOrderLinesAdd.orderErrors[0].message}`
        });
      }
      navigate(storeOrderUrl(id));
      refetch();
    case "UPDATE_ORDER_LINE_STATUS":
      if (data.storeOrderPickLine.orderErrors.length > 0) {
        notify({
          status: "error",
          text: `Something went wrong`
        });
      }
      navigate(storeOrderUrl(id));
      refetch();
    case "UPDATE_ORDER_QUANTITY":
      if (data.storeOrderLineUpdateQuantity.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Changes in Order  ${data.storeOrderLineUpdateQuantity.storeOrder.orderCode} has been saved.`
        });
      } else {
        notify({
          status: "error",
          text: `${data.storeOrderLineUpdateQuantity.orderErrors[0].message}`
        });
      }
      navigate(storeOrderUrl(id));
      refetch();
    case "REVERT_CANCELLED_STATUS":
      if (data.storeOrderRevertFromCancelled.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Cancellation has been reverted!`
        });
      } else {
        notify({
          status: "error",
          text: `${data.storeOrderRevertFromCancelled.orderErrors[0].message}`
        });
      }
      navigate(storeOrderUrl(id));
      refetch();
    default:
      break;
  }
};
